import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Tag,
  Clock3,
  CalendarDays,
  FileText,
  Phone,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { getOffers, isOfferLive } from "@/lib/offersStore";
import { RESTAURANT, COPY } from "@/data/catalog";
import { Seo } from "@/lib/seo";

const dayName = (iso) => {
  // 1=lundi … 7=dimanche
  const names = ["", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  return names[iso] || "";
};

const formatDays = (days) => {
  if (!days || days.length === 0) return null;
  // Détecte une plage continue (ex 1..5 = "Du lundi au vendredi")
  const sorted = [...days].sort((a, b) => a - b);
  const isRange = sorted.every((v, i, arr) => i === 0 || v === arr[i - 1] + 1);
  if (isRange && sorted.length >= 2) {
    const full = ["", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
    return `Du ${full[sorted[0]]} au ${full[sorted[sorted.length - 1]]}`;
  }
  return sorted.map(dayName).join(" · ");
};

const OfferCard = ({ offer, live }) => (
  <article
    id={offer.id}
    data-testid={`offer-card-${offer.id}`}
    className="scroll-mt-24 relative overflow-hidden rounded-2xl border border-[#262626] bg-[#121212]"
  >
    {/* Visuel optionnel */}
    {offer.media_url && offer.media_type === "image" && (
      <div className="relative aspect-[16/9] overflow-hidden border-b border-[#262626]">
        <img
          src={offer.media_url}
          alt={offer.title}
          loading="lazy" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/65 to-transparent" />
        <div className="absolute bottom-1 right-2 text-[10px] text-white/45 italic">
          {COPY.nonContractual}
        </div>
      </div>
    )}

    <div className="p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-street text-2xl sm:text-3xl tracking-wide">
              {offer.title}
            </h3>
            {!live && offer.active && (
              <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-white/55 font-street">
                Hors créneau
              </span>
            )}
          </div>
          {offer.subtitle && (
            <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#FFC447] font-street">
              {offer.subtitle}
            </div>
          )}
          {offer.description && (
            <p className="mt-1 text-sm text-[#A1A1AA] leading-snug">
              {offer.description}
            </p>
          )}
        </div>
        <div className="font-display text-4xl sm:text-5xl leading-none text-[#FF9500] whitespace-nowrap">
          {offer.price}
        </div>
      </div>

      {/* Composition */}
      {offer.composition && offer.composition.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {offer.composition.map((c) => (
            <li
              key={c}
              className="text-sm text-white flex items-start gap-2 leading-snug"
            >
              <CheckCircle2 size={14} className="text-[#FF9500] mt-1 shrink-0" />
              {c}
            </li>
          ))}
        </ul>
      )}

      {/* Disponibilité jours/heures */}
      {(formatDays(offer.active_days) || offer.start_time) && (
        <div className="mt-4 flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.22em] font-street">
          {formatDays(offer.active_days) && (
            <span className="inline-flex items-center gap-1.5 text-[#FFC447]">
              <CalendarDays size={12} />
              {formatDays(offer.active_days)}
            </span>
          )}
          {offer.start_time && offer.end_time && (
            <span className="inline-flex items-center gap-1.5 text-[#FFC447]">
              <Clock3 size={12} />
              {offer.start_time.replace(":", "h")} – {offer.end_time.replace(":", "h")}
            </span>
          )}
        </div>
      )}

      {/* Hint discret */}
      {offer.drink_hint && (
        <div className="mt-3 text-[11px] italic text-white/55 leading-snug">
          {offer.drink_hint}
        </div>
      )}

      {/* Mention conditions */}
      {offer.conditions && (
        <div className="mt-2 text-[10px] italic text-white/40 leading-snug">
          {offer.conditions}
        </div>
      )}

      {/* PDF flyer */}
      {offer.pdf_url && (
        <a
          href={offer.pdf_url}
          target="_blank"
          rel="noreferrer"
          data-testid={`offer-pdf-${offer.id}`}
          className="mt-5 inline-flex items-center gap-2 rounded-md border border-[#FF9500]/40 bg-[#FF9500]/10 px-4 py-2.5 text-sm font-street tracking-wider text-[#FF9500] hover:bg-[#FF9500]/20 transition-colors"
        >
          <FileText size={14} />
          Voir le flyer
        </a>
      )}
    </div>
  </article>
);

const Offres = () => {
  const [offers, setOffers] = useState([]);
  const { hash } = useLocation();

  useEffect(() => {
    setOffers(getOffers());
  }, []);

  useEffect(() => {
    if (!hash || offers.length === 0) return;
    const el = document.getElementById(hash.replace("#", ""));
    if (el) {
      setTimeout(
        () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
        80
      );
    }
  }, [hash, offers]);

  const visibleOffers = useMemo(
    () => offers.filter((o) => o.active && o.show_on_offers_page !== false),
    [offers]
  );

  return (
    <div
      data-testid="offres-root"
      className="mx-auto max-w-5xl px-4 sm:px-6 pt-10 sm:pt-14 pb-24 bg-[#0A0A0A] text-white"
    >
      <Seo
        title="Offres LE 11 - Menu étudiant, menu enfant et bons plans"
        description="Bons plans LE 11 à Villiers-le-Bel : LE 11 DES ÉTUDIANTS (6,50 €, lundi-vendredi 11h-14h), LE P'TIT 11 (4,90 €) et offres temporaires."
      />
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.28em] text-[#FF9500] font-street inline-flex items-center gap-2">
          <Tag size={12} />
          Offres LE 11
        </div>
        <h1 className="font-street mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-wide leading-[0.95]">
          Bons plans <span className="text-[#FF9500]">LE 11</span>
        </h1>
        <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">
          Profitez de nos offres permanentes et de nos bons plans du moment.
          Pour commander, c'est par téléphone.
        </p>

        <div className="mt-5 flex flex-wrap gap-3 items-center">
          <a
            href={RESTAURANT.phoneTel}
            className="font-street inline-flex items-center gap-2 rounded-md bg-[#FF9500] px-5 py-3 text-sm tracking-wider text-black shadow-[0_4px_0_#7a4900] hover:-translate-y-0.5 transition-all"
          >
            <Phone size={16} strokeWidth={2.6} />
            {RESTAURANT.phoneDisplay}
          </a>
          <Link
            to="/menu"
            className="font-street inline-flex items-center gap-2 rounded-md border border-[#FF9500]/40 px-5 py-3 text-sm tracking-wider text-white hover:bg-[#FF9500]/10 transition-colors"
          >
            Voir la carte
          </Link>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#FF9500]/30 bg-[#FF9500]/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#FF9500] font-street">
            <ShieldCheck size={12} />
            {COPY.halalBadge}
          </span>
        </div>
      </div>

      {/* Liste des offres */}
      <div className="mt-10 grid gap-5 sm:grid-cols-2">
        {visibleOffers.length === 0 && (
          <div className="sm:col-span-2 rounded-2xl border border-[#262626] bg-[#121212] p-6 text-center text-sm text-[#A1A1AA]">
            Aucune offre active pour le moment. Revenez bientôt.
          </div>
        )}
        {visibleOffers.map((o) => (
          <OfferCard key={o.id} offer={o} live={isOfferLive(o)} />
        ))}
      </div>
    </div>
  );
};

export default Offres;
